import React, { useEffect } from 'react';

import {
  FeatureFlagManager,
  FeatureFlagProvider as FirebaseFlagProvider,
  useFeatureFlags,
} from '@comcast-dss-utils/feature-flag-manager';
import getConfig from '../../config';
import firebaseLastKnown from './firebaseLastKnown.json';

const { firebaseConfig, firebaseEnabled } = getConfig();
const FLAGS_READY_TIMEOUT_MS = 5000;

let firebaseClient;

const getFirebaseClient = () => {
  if (!firebaseClient) {
    firebaseClient = new FeatureFlagManager(firebaseConfig, {
      defaultFlagValues: firebaseLastKnown,
    });
  }
  return firebaseClient;
};

//  Initialize with last-known defaults, synced with actual values once provider loads
let flagCache = { ...firebaseLastKnown };

// Resolves when flags have been loaded from Firebase
let flagsReadyPromiseResolver;
let hasResolved = false;
let flagsReadyTimer = null;

const updateFlagCache = (flags) => {
  if (flags) flagCache = flags;
};

const resolveWhenReady = () => {
  if (hasResolved) return;
  hasResolved = true;
  if (flagsReadyTimer) {
    clearTimeout(flagsReadyTimer);
    flagsReadyTimer = null;
  }
  flagsReadyPromiseResolver();
};

const startFlagsReadyTimeout = () => {
  if (flagsReadyTimer || hasResolved) return;
  flagsReadyTimer = setTimeout(() => {
    // eslint-disable-next-line no-console
    console.error('firebase_flags_ready_timeout');
    resolveWhenReady();
  }, FLAGS_READY_TIMEOUT_MS);
};

const flagsReadyPromise = new Promise((resolve) => {
  flagsReadyPromiseResolver = resolve;
});

// Eagerly fetch flags from Firebase outside the React tree
if (firebaseEnabled) {
  const client = getFirebaseClient();
  client.getAllFlags().then((flags) => {
    updateFlagCache(flags);
    resolveWhenReady();
  }).catch((error) => {
    // eslint-disable-next-line no-console
    console.error('firebase_getAllFlags_load_failed', {
      errorMessage: error && error.message ? error.message : 'Unknown error',
    });
    resolveWhenReady();
  });
}

export const whenFlagsReady = () => {
  if (firebaseEnabled) {
    startFlagsReadyTimeout();
  }
  return flagsReadyPromise;
};

// Normalize dot-notation flag names to underscore format (Firebase key constraint)
const normalizeFlagName = flagName => flagName.replace(/\./g, '_');

// Returns boolean value of a flag, with undefined treated as false
export const getFlagFromCache = flagName => String(flagCache[normalizeFlagName(flagName)]).toLowerCase() === 'true';

// Returns the raw flag value (string/number) without boolean conversion
export const getFlagValueFromCache = flagName => flagCache[normalizeFlagName(flagName)];

// Syncs flags to module cache via useEffect to avoid side effects during render
const FlagGate = ({ children, fallback = null }) => {
  const { flags, isLoading } = useFeatureFlags();

  useEffect(() => {
    if (!isLoading) {
      updateFlagCache(flags || null);
      resolveWhenReady();
    }
  }, [flags, isLoading]);
  if (isLoading) return fallback;

  return children;
};

// Wraps children with the Firebase provider
export const FeatureFlagProvider = ({ children, fallback }) => {
  const client = getFirebaseClient();

  return (
    <FirebaseFlagProvider client={client}>
      <FlagGate fallback={fallback}>{children}</FlagGate>
    </FirebaseFlagProvider>
  );
};
